// Apple HealthKit adapter — reads step counts only.
//
// Guideline compliance
// --------------------
// * Read-only: we only request `read: ['steps']`. We never write to
//   HealthKit. This narrows the permission sheet the user sees to a
//   single "Steps" toggle and passes Apple Review 2.5.1 as long as the
//   UI clearly labels the source (see the "Apple Health" chip on the
//   My Progress steps section).
// * Never replaces manual entry — HealthKit steps are shown as an
//   additional/alternative source; users can still manually add steps
//   via the existing UI.
//
// Failure modes
// -------------
// The Capacitor plugin can't reliably distinguish "user denied" from
// "no data" — both return an empty result. So the UI treats missing
// data as "not connected" and offers a re-connect button that also
// deep-links the user into iOS Settings → Health → Apps → Calofay.
//
// Timezone
// --------
// HealthKit works in absolute UTC timestamps. We compute LOCAL midnight
// boundaries with `setHours(0,0,0,0)` before converting to ISO so a
// user in Riyadh sees their steps from local 00:00, not UTC.

import { isNativeApp, nativePlatform } from "@/lib/native";

// Static import — same reason as revenuecat.js: Capacitor 8.x dynamic
// imports can hang on plugin proxies. Web bundles are safe because
// every method below early-exits when `isHealthKitEnabled()` is false.
import { CapacitorHealthkit, SampleNames } from "@perfood/capacitor-healthkit";

/** iOS native only. Web + Android + browser preview all return false. */
export function isHealthKitEnabled() {
  return isNativeApp() && nativePlatform() === "ios";
}

/** True if the plugin bridge is registered AND HealthKit is available
 *  on the device (iPad without a step-counter capable device returns false). */
export async function isHealthKitAvailable() {
  if (!isHealthKitEnabled()) return false;
  try {
    await CapacitorHealthkit.isAvailable();
    return true;
  } catch {
    return false;
  }
}

/** Request READ-only permission for step count. Resolves true even
 *  when the user denies — Apple's SDK deliberately hides the decision
 *  for privacy. Callers must probe with a subsequent `queryTodaySteps`
 *  to know whether they can actually read data. */
export async function requestStepReadPermission() {
  if (!(await isHealthKitAvailable())) return false;
  try {
    await CapacitorHealthkit.requestAuthorization({
      read: ["steps"],
      write: [],
      all: [],
    });
    return true;
  } catch (e) {
    console.warn("[HealthKit] requestAuthorization failed", e);
    return false;
  }
}

// --- Internal helpers ------------------------------------------------

function localDayBounds(day = new Date()) {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
}

function sampleValue(s) {
  return Number(s?.value ?? s?.quantity ?? 0);
}

function sampleSource(s) {
  return s?.sourceBundleId || s?.source || "unknown";
}

// HealthKit's `HKSampleQuery` (used by the underlying plugin) returns
// EVERY step sample from EVERY source — iPhone motion coprocessor,
// Apple Watch, and any third-party app the user has permitted. Apple's
// Health app deduplicates via `HKStatisticsQuery` internally; we can't
// call that through the plugin, so we approximate:
//
//   For each day (or window) bucket, pick the SINGLE source with the
//   largest step total. That source is almost always the wrist-worn
//   Apple Watch when present (most accurate) or the iPhone otherwise.
//   This avoids the classic 2×-3× inflation caused by summing iPhone +
//   Watch samples for the same walk.
//
// Rationale for "max source" over "average": Apple Health itself picks
// the *most complete* source (Watch > iPhone > 3rd-party) rather than
// averaging — and the source with the highest count is a reliable proxy
// for that since a partial source will always have a smaller total.
function sumDedupedBySource(samples) {
  if (!Array.isArray(samples) || samples.length === 0) return 0;
  const perSource = new Map();
  for (const s of samples) {
    const src = sampleSource(s);
    perSource.set(src, (perSource.get(src) || 0) + sampleValue(s));
  }
  let max = 0;
  for (const v of perSource.values()) if (v > max) max = v;
  return Math.max(0, Math.round(max));
}

function sum(result) {
  return sumDedupedBySource(result?.resultData || []);
}

// --- Public queries --------------------------------------------------

/**
 * Return today's cumulative step count from HealthKit. Local calendar
 * day. Returns 0 when unavailable OR when the user hasn't granted
 * permission (HealthKit hides denial for privacy — we can't tell
 * these apart, so the UI just shows "0 · Apple Health" if the number
 * looks stuck).
 */
export async function queryTodaySteps() {
  if (!isHealthKitEnabled()) return 0;
  const { start, end } = localDayBounds();
  try {
    const result = await CapacitorHealthkit.queryHKitSampleType({
      sampleName: SampleNames.STEP_COUNT,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      limit: 0,
    });
    return sum(result);
  } catch (e) {
    console.warn("[HealthKit] queryTodaySteps failed", e);
    return 0;
  }
}

/**
 * Backfill last N local calendar days (inclusive of today). Returns
 * `[{ date: 'YYYY-MM-DD', healthKitSteps }]` — one entry per day even
 * if the count is zero, so callers can zip against their own DB
 * rows.
 */
export async function queryLastNDays(n = 7) {
  if (!isHealthKitEnabled()) return [];
  const today = new Date();
  const first = new Date(today);
  first.setHours(0, 0, 0, 0);
  first.setDate(first.getDate() - (n - 1));
  const end = localDayBounds(today).end;

  try {
    const result = await CapacitorHealthkit.queryHKitSampleType({
      sampleName: SampleNames.STEP_COUNT,
      startDate: first.toISOString(),
      endDate: end.toISOString(),
      limit: 0,
    });
    // Bucket samples by local date key AND by source bundle id — then
    // for each day pick the SINGLE source with the largest total.
    // This is the same "max-source" trick used by `sum()` above and
    // is required because HKSampleQuery returns overlapping samples
    // from iPhone + Apple Watch + third-party apps that would
    // otherwise 2×-3× the true step count. See `sumDedupedBySource`.
    const dayKeys = [];
    const daySourceMap = new Map(); // "YYYY-MM-DD" → Map(sourceId → total)
    for (let i = 0; i < n; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      const key = d.toLocaleDateString("en-CA");
      dayKeys.push(key);
      daySourceMap.set(key, new Map());
    }
    for (const s of result?.resultData || []) {
      const src = s?.startDate ? new Date(s.startDate) : null;
      if (!src) continue;
      const key = src.toLocaleDateString("en-CA");
      const sm = daySourceMap.get(key);
      if (!sm) continue;
      const srcId = sampleSource(s);
      sm.set(srcId, (sm.get(srcId) || 0) + sampleValue(s));
    }
    return dayKeys.map((date) => {
      const sm = daySourceMap.get(date);
      let max = 0;
      if (sm) for (const v of sm.values()) if (v > max) max = v;
      return { date, healthKitSteps: Math.max(0, Math.round(max)) };
    });
  } catch (e) {
    console.warn("[HealthKit] queryLastNDays failed", e);
    return [];
  }
}
